/**
 * Iter 41.2 — Public Contact / Schedule section on the landing page.
 * Posts to POST /api/leads (no auth, rate-limited). Bilingual.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Loader2, CheckCircle2, Calendar } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactSection() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    name: '', email: '', company: '', message: '', preferred_date: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((errs) => ({ ...errs, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = t('pages.landing.contact.errName');
    if (!EMAIL_RE.test(form.email.trim())) next.email = t('pages.landing.contact.errEmail');
    if (form.message.trim().length < 5) next.message = t('pages.landing.contact.errMessage');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post('/leads', {
        ...form,
        locale: (i18n.language || 'es').startsWith('es') ? 'es' : 'en',
        source: 'landing-contact',
      });
      setDone(true);
    } catch (err) {
      toast.error(t('pages.landing.contact.failed'), { description: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-subtle border-t border-border" data-testid="contact-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs uppercase tracking-wider font-semibold text-primary mb-3">
            <Calendar className="h-3.5 w-3.5" />
            <span>{t('pages.landing.contact.eyebrow')}</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            {t('pages.landing.contact.heading')}
          </h2>
          <p className="mt-3 text-base text-muted-foreground">
            {t('pages.landing.contact.subheading')}
          </p>
        </div>

        <Card className="mt-12 p-6 lg:p-10 shadow-card">
          {done ? (
            <div className="py-10 text-center" data-testid="contact-success">
              <div className="h-14 w-14 rounded-full bg-success/15 text-success flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <p className="font-display text-2xl font-bold tracking-tight mt-5">
                {t('pages.landing.contact.successTitle')}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {t('pages.landing.contact.successBody')}
              </p>
            </div>
          ) : (
            <form className="grid sm:grid-cols-2 gap-5" onSubmit={submit} data-testid="contact-form">
              <div className="space-y-1.5 sm:col-span-1">
                <Label htmlFor="lead-name">{t('pages.landing.contact.name')}<span className="text-destructive">*</span></Label>
                <Input id="lead-name" data-testid="lead-name" value={form.name} onChange={set('name')} aria-invalid={!!errors.name} />
                {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
              </div>
              <div className="space-y-1.5 sm:col-span-1">
                <Label htmlFor="lead-email">{t('pages.landing.contact.email')}<span className="text-destructive">*</span></Label>
                <Input id="lead-email" type="email" data-testid="lead-email" value={form.email} onChange={set('email')} aria-invalid={!!errors.email} />
                {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
              </div>
              <div className="space-y-1.5 sm:col-span-1">
                <Label htmlFor="lead-company">{t('pages.landing.contact.company')}</Label>
                <Input id="lead-company" data-testid="lead-company" value={form.company} onChange={set('company')} />
              </div>
              <div className="space-y-1.5 sm:col-span-1">
                <Label htmlFor="lead-date">{t('pages.landing.contact.preferredDate')}</Label>
                <Input id="lead-date" data-testid="lead-date" placeholder={t('pages.landing.contact.preferredDatePh')} value={form.preferred_date} onChange={set('preferred_date')} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="lead-message">{t('pages.landing.contact.message')}<span className="text-destructive">*</span></Label>
                <Textarea id="lead-message" data-testid="lead-message" value={form.message} onChange={set('message')} rows={5} aria-invalid={!!errors.message} />
                {errors.message && <p className="text-xs text-destructive">{errors.message}</p>}
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <Button type="submit" size="lg" disabled={submitting} className="gap-1.5" data-testid="lead-submit">
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> {t('pages.landing.contact.submitting')}</>
                              : <><Mail className="h-4 w-4" /> {t('pages.landing.contact.submit')}</>}
                </Button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </section>
  );
}
